import React, { ReactNode, useLayoutEffect, useRef, useState } from 'react'

import { RATING_POLICIES } from '@digital-twins/shared-types'

import { AssetFrame } from '../../components/primitives/AssetFrame/AssetFrame'
import { LightboxDialog } from '../../components/primitives/LightboxImage/LightboxImage'
import { DECK_CHART_PRIMARY_COLOR } from '../../foundations/chart-colors'
import { TextClamp } from '../../components/text/TextClamp'
import { FastTooltip } from '../_shared/FastTooltip'
import { rankBarPercent } from '../ranking-bars/RankingBarsPlot'
import { formatPodiumScore, formatPodiumShare } from '../../slides/podium-presentation'
import {
  PODIUM_ACCENT,
  PODIUM_ACCENT_SOFT,
  PODIUM_PLOT_METRICS,
  PODIUM_PLOT_PALETTE,
  PODIUM_QUOTE_FONT,
  PodiumPlotMetrics,
  PodiumPlotPalette,
  PodiumPlotSize,
} from './podium-metrics'

import { RankItem } from '@digital-twins/slide-schemas/generated/plot-types'
import { StoredPlotPayload } from '../contract'

/**
 * The podium plot (plots plan E): every ranked tested item in the same frame,
 * in rank order, the escape rows optionally as bars beneath. One component for
 * the deck slide (`size: 'slide'`) and the summary card (`size: 'card'`).
 * Text items (pain points, claims) are not tested assets and draw as ranking
 * bars (`layout: 'bars'`): the board is for tested assets.
 *
 * It draws Python's stamps and computes none: the rank on each item sets the
 * order and the chip, `topIds`/`tie` set the crown (a tie removes it and shows
 * "too close to call"), `leadChip` names a crown's strength or its small base,
 * `decimals` sets the printed figure and the score bar,
 * and pictures arrive already resolved by the Images rule (each row its own).
 * Every picture sits in an `AssetFrame`, never cropped; ⤢ opens it through
 * the existing lightbox. An imageless item draws, in the same frame, the copy
 * respondents read (`respondentCopy`) as a pull quote, else the placeholder.
 * Names, figures and bars sit on one grid across the board, so they line up
 * whatever each name's length. Every bar is drawn on the figure's real
 * scale (`podiumBarScale`).
 */

export type PodiumPlotData = StoredPlotPayload<'podium'>['data']

export interface PodiumPlotLabelArgs {
  item: RankItem
  /** The item's index in `data.items` (the deck's edit field path). */
  index: number
  maxLines: number
  style: React.CSSProperties
}

export interface PodiumPlotViewProps {
  data: PodiumPlotData
  size?: PodiumPlotSize
  /** `board`: tested assets in equal frames. `bars`: every row a ranking bar. */
  layout?: 'board' | 'bars'
  /** The escape rows ("None", "Don't know") under the board. */
  tailAs?: 'bars' | 'hidden'
  renderMode?: 'interactive' | 'static'
  /** The crown text; default `plot_rank_n` for rank 1 followed by `winner`. */
  crownLabel?: string
  /** The deck passes its editable text; everyone else gets a clamped label with hover. */
  renderLabel?: (args: PodiumPlotLabelArgs) => ReactNode
  /** Legacy stored cards print today's figure string; new payloads never set it. */
  formatValue?: (item: RankItem) => string | null
  /** The board's test id. The deck slide keeps `podium-plinth`. */
  testId?: string
  /** Bars layout only: the heading drawn beside the tie badge, as today's bars drew it. */
  heading?: string | null
  /** Bars layout only: the caller's label formatter (the frontend passes its `formatLabel`). */
  formatItemLabel?: (label: string) => string
  /** Bars layout only: what an empty ranking says (today's "no ranking data"). */
  emptyLabel?: string
}

/**
 * The card's columns: two on a phone, the board's own count from `sm` up. The
 * strings are literal so the app's Tailwind build sees every class.
 */
const CARD_GRID_COLUMNS: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4',
}

function fillTemplate(template: string | undefined, values: Record<string, string | number>): string | null {
  if (!template) {
    return null
  }
  return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in values ? String(values[key]) : match))
}

/**
 * The board's one chip: print-equal leaders read "too close to call"; a
 * crowned board shows Python's `leadChip` (its strength, or "Small base
 * (n = N)" filled with the board's base). None when neither is stamped.
 */
export function podiumChipText(data: PodiumPlotData): string | null {
  if (data.tie) {
    return data.labels.too_close_to_call ?? null
  }
  return data.leadChip ? fillTemplate(data.labels[data.leadChip], { n: data.base ?? '' }) : null
}

function defaultFigure(item: RankItem, data: PodiumPlotData): string | null {
  return data.unit === 'percent_0_100'
    ? formatPodiumShare(item.value, data.decimals)
    : formatPodiumScore(item.value, data.decimals)
}

/** The tile's picture in its frame. Interactive: the lightbox trigger plus a ⤢ affordance. */
function TileImage(props: {
  item: RankItem
  index: number
  metrics: PodiumPlotMetrics
  palette: PodiumPlotPalette
  isInteractive: boolean
  testId: string
  style: React.CSSProperties
}) {
  const { item, index, metrics, palette, isInteractive, testId, style } = props

  return (
    <AssetFrame
      url={item.imageUrl ?? ''}
      kind={item.previewKind ?? 'image'}
      alt={item.label}
      lightbox={isInteractive}
      playSize={metrics.playSize}
      data-testid={testId}
      pictureTestId={isInteractive ? `podium-lightbox-${index}` : `podium-picture-${index}`}
      playTestId={`podium-play-${index}`}
      style={{ width: '100%', border: `1px solid ${palette.border}`, boxSizing: 'border-box', ...style }}
    >
      {isInteractive && <ExpandMark index={index} metrics={metrics} palette={palette} />}
    </AssetFrame>
  )
}

/**
 * The ⤢ in a tile's corner: the tile opens whole on click. A picture keeps it
 * bottom right; a quote's copy runs to the bottom, so its mark sits top left,
 * clear of the rank chip and the copy.
 */
function ExpandMark(props: { index: number; metrics: PodiumPlotMetrics; palette: PodiumPlotPalette; corner?: 'bottom-right' | 'top-left' }) {
  const { index, metrics, palette, corner = 'bottom-right' } = props
  return (
    <span
      data-testid={`podium-expand-${index}`}
      aria-hidden="true"
      style={{
        position: 'absolute',
        ...(corner === 'top-left'
          ? { left: metrics.badgeInset, top: metrics.badgeInset }
          : { right: metrics.badgeInset, bottom: metrics.badgeInset }),
        width: metrics.expandSize,
        height: metrics.expandSize,
        borderRadius: 999,
        background: palette.chipBg,
        color: palette.chipFg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(metrics.expandSize * 0.55),
        lineHeight: 1,
        pointerEvents: 'none',
      }}
    >
      ⤢
    </span>
  )
}

/**
 * The scale every bar is drawn on (owner, 2026-10-18: a full bar is the
 * figure's real top, never the leader): Python's stamped `scale`
 * (`podium_board.board_scale`: 0-100 for a share, the rating unit's bounds for
 * a mean, 1..N for a ranking question's weighted score), the ranking bars'
 * scale. Legacy reader only: a board stored before the stamp, or a composite
 * board, reads its unit: 0-100 for a share, up to a rating scale's top, else
 * up to its largest entry.
 */
export function podiumBarScale(data: Pick<PodiumPlotData, 'unit' | 'items' | 'scale'>): { min: number; max: number } {
  if (data.scale) {
    return data.scale
  }
  if (data.unit === 'percent_0_100') {
    return { min: 0, max: 100 }
  }
  const policy = RATING_POLICIES[data.unit]
  return { min: 0, max: policy ? policy.maximum : Math.max(...data.items.map((item) => item.value), 0) || 1 }
}

/** A bar's filled width in percent of its track, on the board's scale (`rankBarPercent`). */
function barWidth(value: number, scale: { min: number; max: number }): string {
  return `${rankBarPercent(value, scale)}%`
}

/**
 * An imageless entrant's copy as a pull quote: a quote mark, then the words in
 * the deck's serif, inside the picture's frame. The copy is word for word and
 * never shortened: it fills the lines its frame holds, measured in the page,
 * and a longer copy ends with an ellipsis. An interactive board opens the
 * whole copy on click or Enter (the picture tiles' ⤢ and lightbox); a static
 * render shows the clamp.
 */
function PodiumCopyTile(props: {
  item: RankItem
  index: number
  copy: string
  metrics: PodiumPlotMetrics
  palette: PodiumPlotPalette
  isInteractive: boolean
}) {
  const { item, index, copy, metrics, palette, isInteractive } = props
  const bodyRef = useRef<HTMLDivElement>(null)
  const markHeight = Math.round(metrics.quoteMarkSize * 0.5)
  const [lines, setLines] = useState<number | null>(null)
  const [open, setOpen] = useState(false)

  useLayoutEffect(() => {
    const element = bodyRef.current
    const text = element?.lastElementChild
    if (!element || !text) {
      return
    }
    const measure = () => {
      // The quote takes the frame's height inside its padding; the clamp is
      // the whole lines left under the quote mark.
      const lineHeight = parseFloat(window.getComputedStyle(text).lineHeight)
      const fitted = lineHeight > 0 ? Math.floor((element.clientHeight - markHeight) / lineHeight) : 0
      setLines(fitted > 0 ? fitted : null)
    }
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(element)
    void document.fonts?.ready.then(measure)
    return () => observer?.disconnect()
  }, [markHeight])

  const openFull = () => setOpen(true)
  return (
    <>
      <div
        data-testid={`podium-copy-${index}`}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        aria-label={isInteractive ? item.label : undefined}
        onClick={isInteractive ? openFull : undefined}
        onKeyDown={isInteractive ? (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openFull()
          }
        } : undefined}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: metrics.frameAspect,
          borderRadius: 10,
          background: palette.cardBg,
          border: `1px solid ${palette.border}`,
          boxSizing: 'border-box',
          padding: metrics.copyPadding,
          display: 'flex',
          flexDirection: 'column',
          cursor: isInteractive ? 'zoom-in' : undefined,
        }}
      >
        <div ref={bodyRef} style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <span
            aria-hidden="true"
            data-testid={`podium-copy-mark-${index}`}
            style={{
              flexShrink: 0,
              height: markHeight,
              fontFamily: PODIUM_QUOTE_FONT,
              fontSize: metrics.quoteMarkSize,
              lineHeight: 1,
              color: PODIUM_ACCENT,
            }}
          >
            “
          </span>
          <p
            data-testid={`podium-copy-text-${index}`}
            style={{
              margin: 0,
              overflow: 'hidden',
              fontFamily: PODIUM_QUOTE_FONT,
              fontSize: metrics.copyFontSize,
              lineHeight: metrics.copyLineHeight,
              color: palette.text,
              whiteSpace: 'pre-line',
              ...(metrics.fitNarrowTiles ? { overflowWrap: 'anywhere' as const } : {}),
              ...(lines ? { display: '-webkit-box', WebkitBoxOrient: 'vertical' as const, WebkitLineClamp: lines } : {}),
            }}
          >
            {copy}
          </p>
        </div>
        {isInteractive && <ExpandMark index={index} metrics={metrics} palette={palette} corner="top-left" />}
      </div>
      {open ? (
        <LightboxDialog label={item.label} onClose={() => setOpen(false)}>
          <figure
            data-testid={`podium-copy-full-${index}`}
            style={{
              margin: 0,
              boxSizing: 'border-box',
              width: 'min(640px, 90vw)',
              maxHeight: '85vh',
              overflow: 'auto',
              padding: '28px 32px',
              borderRadius: 12,
              background: 'var(--slide-bg)',
              color: 'var(--slide-text-primary)',
              boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
              cursor: 'default',
            }}
          >
            <span aria-hidden="true" style={{ display: 'block', height: 28, fontFamily: PODIUM_QUOTE_FONT, fontSize: 56, lineHeight: 1, color: PODIUM_ACCENT }}>
              “
            </span>
            <blockquote style={{ margin: 0, fontFamily: PODIUM_QUOTE_FONT, fontSize: 22, lineHeight: 1.45, whiteSpace: 'pre-line' }}>
              {copy}
            </blockquote>
            <figcaption style={{ marginTop: 16, fontSize: 14, fontWeight: 600 }}>{item.label}</figcaption>
          </figure>
        </LightboxDialog>
      ) : null}
    </>
  )
}

/** The row's tile: the picture when there is one, else the item letter or the label's first letter. */
function BarTile(props: { item: RankItem; label: string; testId: string }) {
  const { item, label, testId } = props

  if (item.imageUrl) {
    return (
      <AssetFrame
        url={item.imageUrl}
        kind={item.previewKind ?? 'image'}
        alt={label}
        radius={8}
        data-testid={testId}
        className="w-9 h-9 flex-shrink-0 border border-border/40"
      />
    )
  }
  const letter = item.tag ?? (label.trim().charAt(0).toUpperCase() || '?')
  return (
    <span
      data-testid={testId}
      className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center text-sm font-bold bg-muted/40 text-foreground/70"
    >
      {letter}
    </span>
  )
}

interface PodiumBarNameProps {
  label: string
  isStatic: boolean
}

/** Static bars keep the native title on the clamp. Interactive bars use the 150ms tooltip. */
function PodiumBarName(props: PodiumBarNameProps) {
  const { label, isStatic } = props
  if (isStatic) {
    return (
      <TextClamp maxLines={2} className="text-sm leading-snug flex-1 min-w-0 font-normal text-foreground" title={label}>
        {label}
      </TextClamp>
    )
  }
  return (
    <FastTooltip label={label}>
      <span className="flex-1 min-w-0">
        <TextClamp maxLines={2} className="text-sm leading-snug font-normal text-foreground">
          {label}
        </TextClamp>
      </span>
    </FastTooltip>
  )
}

/**
 * Text items as ranking bars (decision 18: they look as they do today). The
 * markup and classes are today's `RankingChartView variant="podium"`, fed by
 * the stamps: the stamped rank, the crown on `topIds`, the stamped figure.
 */
function PodiumBars(props: {
  data: PodiumPlotData
  crownedIds: ReadonlySet<string>
  heading?: string | null
  formatItemLabel?: (label: string) => string
  emptyLabel?: string
  figureOf: (item: RankItem) => string | null
  isStatic: boolean
  renderLabel?: (args: PodiumPlotLabelArgs) => ReactNode
}) {
  const { data, crownedIds, heading, formatItemLabel, emptyLabel, figureOf, isStatic, renderLabel } = props

  if (data.items.length === 0) {
    return (
      <div className="flex items-center justify-center text-muted-foreground min-h-48" data-testid="ranking-chart-view">
        {emptyLabel}
      </div>
    )
  }

  const format = formatItemLabel ?? ((label: string) => label)
  const barScale = podiumBarScale(data)
  const ranked = data.items.filter((item) => !item.isEscape)
  const escapes = data.items.filter((item) => item.isEscape)
  const chipText = podiumChipText(data)
  const barLabel = (item: RankItem, label: string) => renderLabel ? (
    <div style={{ flex: 1, minWidth: 0 }}>
      {renderLabel({ item, index: data.items.indexOf(item), maxLines: 2, style: { fontSize: 14, lineHeight: 1.3, color: 'var(--slide-text)' } })}
    </div>
  ) : <PodiumBarName label={label} isStatic={isStatic} />

  return (
    <div className="w-full" data-testid="ranking-chart-view">
      {(heading || chipText) && (
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-sm font-medium text-muted-foreground">{heading ?? ''}</span>
          {chipText && (
            <span
              data-testid={data.tie ? 'ranking-podium-tie' : 'ranking-podium-lead'}
              className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-muted/60 text-foreground/70"
            >
              {chipText}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-col gap-4">
        {ranked.map((item, index) => {
          const position = index + 1
          const isWinner = crownedIds.has(item.id)
          const label = format(item.label)
          return (
            <div
              key={item.id}
              data-testid={`ranking-podium-row-${position}`}
              data-escape="false"
              className="flex flex-col gap-1.5"
            >
              <div className="flex items-center gap-3">
                <span
                  data-testid={`ranking-podium-rank-${position}`}
                  className={`text-[11px] font-bold flex-shrink-0 tabular-nums whitespace-nowrap rounded-full px-2 py-0.5 ${isWinner ? 'text-white' : 'text-muted-foreground'}`}
                  style={isWinner ? { backgroundColor: DECK_CHART_PRIMARY_COLOR } : undefined}
                >
                  {fillTemplate(data.labels.plot_rank_n, { rank: item.rank }) ?? `#${item.rank}`}
                </span>
                <BarTile item={item} label={label} testId={`ranking-podium-tile-${position}`} />
                {barLabel(item, label)}
                <span className="text-sm font-normal text-foreground tabular-nums flex-shrink-0">
                  {figureOf(item) ?? String(item.value)}
                </span>
              </div>
              <div className="w-full bg-muted/30 rounded-full overflow-hidden h-2">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{ width: barWidth(item.value, barScale), backgroundColor: DECK_CHART_PRIMARY_COLOR }}
                />
              </div>
            </div>
          )
        })}
        {escapes.length > 0 && (
          <div
            data-testid="ranking-podium-escapes"
            className={`flex flex-col gap-4 opacity-60${ranked.length > 0 ? ' border-t border-border/50 pt-4' : ''}`}
          >
            {escapes.map((item, index) => {
              const label = format(item.label)
              return (
                <div
                  key={item.id}
                  data-testid={`ranking-podium-escape-${index}`}
                  data-escape="true"
                  className="flex flex-col gap-1.5"
                >
                  <div className="flex items-center gap-3">
                    {barLabel(item, label)}
                    <span className="text-sm font-normal text-foreground tabular-nums flex-shrink-0">
                      {figureOf(item) ?? String(item.value)}
                    </span>
                  </div>
                  <div className="w-full bg-muted/30 rounded-full overflow-hidden h-2">
                    <div
                      className="h-full rounded-full"
                      style={{ width: barWidth(item.value, barScale), backgroundColor: 'var(--slide-text-muted)' }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export function PodiumPlotView(props: PodiumPlotViewProps) {
  const {
    data,
    size = 'slide',
    layout = 'board',
    tailAs = 'hidden',
    renderMode = 'interactive',
    crownLabel,
    renderLabel,
    formatValue,
    testId,
    heading,
    formatItemLabel,
    emptyLabel,
  } = props

  const metrics = PODIUM_PLOT_METRICS[size]
  const palette = PODIUM_PLOT_PALETTE[size]
  const isInteractive = renderMode !== 'static'
  const labels = data.labels
  // Every ranked entrant is on the board; the tail is the escape rows only.
  const board = data.items.filter((item) => !item.isEscape)
  const tail = tailAs === 'bars' ? data.items.filter((item) => item.isEscape) : []
  // No tie: every `topIds` member is crowned (one winner, or a lead set of k
  // the primary allows). A tie crowns nobody.
  const crownedIds: ReadonlySet<string> = new Set(data.tie ? [] : data.topIds)
  // One crowned entrant reads as the winner; a lead set's entrants each keep
  // their rank.
  const winnerText = crownLabel
    ?? (labels.winner ? fillTemplate(labels.plot_rank_winner, { winner: labels.winner }) : null)
    ?? fillTemplate(labels.plot_rank_n, { rank: 1 })
  const crownTextOf = (item: RankItem) =>
    crownedIds.size === 1 ? winnerText : fillTemplate(labels.plot_rank_n, { rank: item.rank })
  // The words an imageless tile shows: the copy respondents read, if any.
  const copyOf = (item: RankItem) => (item.imageUrl ? null : item.respondentCopy?.trim() || null)
  const indexOf = (item: RankItem) => data.items.indexOf(item)

  const figureOf = (item: RankItem) => (formatValue ? formatValue(item) : defaultFigure(item, data))
  const scoreLineOf = (item: RankItem) => {
    const figure = figureOf(item)
    if (figure === null) {
      return null
    }
    // The slide names the figure beside it; the card names it once above the board.
    const gloss = size === 'slide' ? data.figureLabel?.trim() : null
    return gloss ? `${figure} ${gloss}` : figure
  }
  const labelNode = (item: RankItem, maxLines: number, isWinner: boolean) => {
    const style: React.CSSProperties = {
      fontSize: metrics.labelFontSize,
      fontWeight: isWinner ? 700 : 600,
      color: palette.text,
      ...(metrics.fitNarrowTiles ? { maxWidth: '100%', overflowWrap: 'anywhere' as const } : {}),
    }
    if (renderLabel) {
      return renderLabel({ item, index: indexOf(item), maxLines, style })
    }
    return (
      <TextClamp minLines={1} maxLines={maxLines} lineHeight="1.3" tooltipOnTruncate renderMode={renderMode} style={style}>
        {item.label}
      </TextClamp>
    )
  }

  const barScale = podiumBarScale(data)
  // One ranking-bar row: the tail under the board, and every row of the bars
  // layout. The bar is the brand's primary chart colour; an escape row is
  // muted, carries no rank and no letter, and is never crowned.
  const barRow = (item: RankItem, testIds: { row: string; rank: string; tile: string }) => {
    const isWinner = crownedIds.has(item.id)
    const rankChip = fillTemplate(labels.plot_rank_n, { rank: item.rank })
    return (
      <div
        key={item.id}
        data-testid={testIds.row}
        data-escape={item.isEscape ? 'true' : 'false'}
        style={{ display: 'flex', flexDirection: 'column', gap: 6, opacity: item.isEscape ? 0.7 : 1 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {!item.isEscape && rankChip && (
            <span
              data-testid={testIds.rank}
              style={{
                fontSize: metrics.rankFontSize,
                fontWeight: 700,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                borderRadius: 999,
                padding: '2px 8px',
                color: isWinner ? '#FFFFFF' : palette.muted,
                background: isWinner ? DECK_CHART_PRIMARY_COLOR : 'transparent',
              }}
            >
              {rankChip}
            </span>
          )}
          {!item.isEscape && item.tag ? (
            <span
              data-testid={testIds.tile}
              style={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: metrics.rankFontSize + 1,
                fontWeight: 700,
                color: palette.text,
                background: palette.cardBg,
                opacity: 0.8,
              }}
            >
              {item.tag}
            </span>
          ) : null}
          <div style={{ flex: 1, minWidth: 0 }}>{labelNode(item, 2, false)}</div>
          <span style={{ fontSize: metrics.scoreFontSize, fontWeight: 600, color: item.isEscape ? palette.muted : palette.text, flexShrink: 0 }}>
            {figureOf(item)}
          </span>
        </div>
        <div style={{ height: 6, borderRadius: 999, background: palette.barTrack, overflow: 'hidden' }}>
          <div
            style={{
              width: barWidth(item.value, barScale),
              height: '100%',
              borderRadius: 999,
              background: item.isEscape ? palette.muted : DECK_CHART_PRIMARY_COLOR,
            }}
          />
        </div>
      </div>
    )
  }

  const chipText = podiumChipText(data)
  const chipBanner = chipText ? (
    <div
      data-testid={data.tie ? 'podium-tie-banner' : 'podium-lead-chip'}
      style={{
        alignSelf: 'center',
        background: PODIUM_ACCENT_SOFT,
        color: PODIUM_ACCENT,
        borderRadius: 999,
        padding: metrics.tiePadding,
        fontSize: metrics.tieFontSize,
        fontWeight: 600,
      }}
    >
      {chipText}
    </div>
  ) : null

  if (layout === 'bars') {
    return (
      <PodiumBars
        data={data}
        crownedIds={crownedIds}
        heading={heading}
        formatItemLabel={formatItemLabel}
        emptyLabel={emptyLabel}
        figureOf={figureOf}
        isStatic={renderMode === 'static'}
        renderLabel={renderLabel}
      />
    )
  }

  const tailNode = tail.length > 0 ? (
    <div data-testid="podium-tail" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
      {tail.map((item, index) => barRow(item, {
        row: item.isEscape ? `podium-tail-escape-${index}` : `podium-tail-row-${index}`,
        rank: `podium-tail-rank-${index}`,
        tile: `podium-tail-tag-${index}`,
      }))}
    </div>
  ) : null

  if (board.length === 1) {
    const entry = board[0]
    const scoreLine = scoreLineOf(entry)
    return (
      <div data-testid={testId ?? 'podium-plinth'} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div data-testid="podium-single-score" style={{ display: 'flex', justifyContent: 'center', flex: 1, alignItems: 'center' }}>
          <article
            data-testid="podium-entry-0"
            style={{
              background: palette.cardBg,
              border: `1px solid ${palette.border}`,
              borderRadius: 12,
              padding: '24px 28px',
              display: 'flex',
              alignItems: 'center',
              gap: 20,
              maxWidth: 460,
            }}
          >
            {entry.imageUrl ? (
              <div style={{ width: metrics.singleTile, flexShrink: 0 }}>
                <TileImage item={entry} index={0} metrics={metrics} palette={palette} isInteractive={isInteractive} testId="podium-image-0" style={{ aspectRatio: metrics.frameAspect }} />
              </div>
            ) : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              {renderLabel
                ? renderLabel({ item: entry, index: indexOf(entry), maxLines: 2, style: { fontSize: metrics.singleLabelFontSize, fontWeight: 700, color: palette.text } })
                : (
                  <TextClamp minLines={1} maxLines={2} lineHeight="1.3" tooltipOnTruncate renderMode={renderMode} style={{ fontSize: metrics.singleLabelFontSize, fontWeight: 700, color: palette.text }}>
                    {entry.label}
                  </TextClamp>
                )}
              {scoreLine !== null && (
                <span style={{ fontSize: metrics.singleScoreFontSize, color: PODIUM_ACCENT, fontWeight: 500, lineHeight: 1.3 }}>
                  {scoreLine}
                </span>
              )}
            </div>
          </article>
        </div>
        {tailNode}
      </div>
    )
  }

  // One row up to `maxColumns`, then wrap; the card drops to two columns on a
  // phone. A short board centres at its frames' widest instead of stretching.
  const columns = Math.min(board.length, metrics.maxColumns)
  const boardMaxWidth = columns * metrics.maxFrameWidth + (columns - 1) * metrics.gap

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div
        data-testid={testId ?? 'podium-plinth'}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: chipBanner ? 12 : 0,
          flex: 1,
          minHeight: 0,
        }}
      >
        {chipBanner}
        <div
          data-testid="podium-grid"
          data-columns={columns}
          className={size === 'card' ? CARD_GRID_COLUMNS[columns] : undefined}
          style={{
            display: 'grid',
            gap: metrics.gap,
            width: '100%',
            maxWidth: boardMaxWidth,
            margin: '0 auto',
            ...(size === 'card' ? {} : { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }),
          }}
        >
          {board.map((item, index) => {
            const isWinner = crownedIds.has(item.id)
            const scoreLine = scoreLineOf(item)
            const rankChip = fillTemplate(labels.plot_rank_n, { rank: item.rank })
            const copy = copyOf(item)
            // No picture and no copy: the tile names the item inside its frame.
            const nameInside = !item.imageUrl && !copy

            return (
              // One entrant on the board's shared grid: four rows (tile, name,
              // figure, bar) that every entrant shares, so the names, figures
              // and bars line up across the row whatever each name's length.
              <div
                key={item.id}
                data-testid={`podium-entry-${index}`}
                style={{ display: 'grid', gridRow: 'span 4', gridTemplateRows: 'subgrid', rowGap: 0, minWidth: 0 }}
              >
                <div style={{ position: 'relative', width: '100%' }}>
                  {item.imageUrl ? (
                    <TileImage item={item} index={index} metrics={metrics} palette={palette} isInteractive={isInteractive} testId={`podium-image-${index}`} style={{ aspectRatio: metrics.frameAspect }} />
                  ) : copy ? (
                    <PodiumCopyTile item={item} index={index} copy={copy} metrics={metrics} palette={palette} isInteractive={isInteractive} />
                  ) : (
                    <div
                      data-testid={`podium-placeholder-${index}`}
                      style={{
                        width: '100%',
                        aspectRatio: metrics.frameAspect,
                        borderRadius: 10,
                        background: palette.cardBg,
                        border: `1px solid ${palette.border}`,
                        boxSizing: 'border-box',
                        padding: metrics.placeholderNamePadding,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {labelNode(item, metrics.placeholderNameLines, isWinner)}
                    </div>
                  )}

                  {isWinner && crownTextOf(item) && (
                    <div
                      data-testid={`podium-winner-badge-${index}`}
                      style={{
                        position: 'absolute',
                        top: metrics.badgeInset,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: PODIUM_ACCENT_SOFT,
                        color: PODIUM_ACCENT,
                        borderRadius: 999,
                        padding: metrics.winnerPadding,
                        fontSize: metrics.winnerFontSize,
                        fontWeight: 700,
                        lineHeight: 1.4,
                        whiteSpace: metrics.fitNarrowTiles ? 'normal' : 'nowrap',
                        ...(metrics.fitNarrowTiles
                          ? { maxWidth: `calc(100% - ${metrics.badgeInset * 2}px)`, textAlign: 'center' as const }
                          : {}),
                      }}
                    >
                      {crownTextOf(item)}
                    </div>
                  )}
                  {!isWinner && rankChip && (
                    <div
                      data-testid={`podium-rank-badge-${index}`}
                      style={{
                        position: 'absolute',
                        top: metrics.badgeInset,
                        right: metrics.badgeInset,
                        background: palette.chipBg,
                        color: palette.chipFg,
                        borderRadius: 999,
                        padding: metrics.rankPadding,
                        fontSize: metrics.rankFontSize,
                        fontWeight: 700,
                        lineHeight: 1.4,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {rankChip}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: metrics.entryGap, minWidth: 0 }}>
                  {nameInside ? null : labelNode(item, 2, isWinner)}
                </div>
                <div style={{ marginTop: 3, minWidth: 0 }}>
                  {scoreLine !== null && (
                    <span data-testid={`podium-score-${index}`} style={{ fontSize: metrics.scoreFontSize, color: PODIUM_ACCENT, fontWeight: 500, lineHeight: 1.3 }}>
                      {scoreLine}
                    </span>
                  )}
                </div>
                <div data-testid={`podium-score-bar-${index}`} style={{ alignSelf: 'start', width: '100%', height: 6, borderRadius: 999, background: palette.barTrack, overflow: 'hidden', marginTop: 5 }}>
                  <div
                    style={{
                      width: barWidth(item.value, barScale),
                      height: '100%',
                      borderRadius: 999,
                      background: DECK_CHART_PRIMARY_COLOR,
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
      {tailNode}
    </div>
  )
}
